import { IcoCheckCircle16, IcoPlusSmall16 } from '@onefootprint/icons';
import { LinkButton } from '@onefootprint/ui';
import { useRef, useState } from 'react';
import type { AgentChecklistBlockConfig } from '@/components/block-primitives/contracts/agent-blocks';
import BlockShell from '../block-shell';
import ListRow from '../list-row';
import TitleField from '../title-field';
type AgentChecklistBlockProps = {
  config: AgentChecklistBlockConfig;
  editable?: boolean;
  justInserted?: boolean;
  onChange?: (next: AgentChecklistBlockConfig) => void;
  onDelete?: () => void;
  onExit?: () => void;
  requireItems?: boolean;
};
const AgentChecklistBlock = ({
  config,
  editable = false,
  justInserted = false,
  onChange,
  onDelete,
  onExit,
  requireItems = false
}: AgentChecklistBlockProps) => {
  const [pending, setPending] = useState(false);
  const everFilled = useRef(false);
  const firstItem = useRef<{
    focus: () => void;
  }>(null);
  everFilled.current ||= config.items.length > 0;
  const rows = rowsFor(config.items, editable);
  const removable = rows.length > 1;
  const patch = (next: Partial<AgentChecklistBlockConfig>) => {
    onChange?.({
      ...config,
      ...next
    });
  };
  const removeItem = (index: number) => {
    if (index >= config.items.length) return;
    patch({
      items: config.items.filter((_, i) => i !== index)
    });
  };
  const abandonRow = (index_0: number) => {
    if (!requireItems && justInserted && !everFilled.current) {
      onDelete?.();
      return;
    }
    removeItem(index_0);
  };
  const submitItem = (index_1: number, label: string) => {
    if (!label) return;
    patch({
      items: rows.map((item, i_0) => i_0 === index_1 ? label : item)
    });
  };
  const submitItemLines = (index_2: number, lines: string[]) => {
    patch({
      items: [...rows.slice(0, index_2), ...lines, ...rows.slice(index_2 + 1)]
    });
  };
  const submitPending = (label_0: string) => {
    if (!label_0) {
      setPending(false);
      return;
    }
    patch({
      items: [...config.items, label_0]
    });
  };
  const blockDeletionCopy = () => {
    const count = config.items.length;
    if (!count) {
      return 'It holds no items yet.';
    }
    if (count === 1) {
      return 'Its only item goes with it, and the agent will no longer be asked to hold to it.';
    }
    return `Its ${count} items go with it, and the agent will no longer be asked to hold to them.`;
  };
  return <BlockShell onExit={onExit} editable={editable} label="Checklist" onDelete={onDelete && {
    confirmation: {
      title: 'Delete checklist?',
      description: blockDeletionCopy()
    },
    onConfirm: onDelete
  }} title={config.title ?? undefined}>
      <TitleField autoFocus={requireItems && justInserted} editable={editable} label="Checklist title" onEnter={requireItems ? () => firstItem.current?.focus() : undefined} onSubmit={title => patch({
      title: title || undefined
    })} value={config.title ?? undefined} />

      {(rows.length > 0 || pending) && <ul className="flex flex-col gap-0.5">
          {rows.map((item_0, index_3) => <ListRow autoFocus={!requireItems && justInserted && !config.items.length} editable={editable} editorRef={index_3 === 0 ? firstItem : undefined}
      // biome-ignore lint/suspicious/noArrayIndexKey: items are user-editable strings, so position is the stable identity
      key={index_3} label="Item" marker={<IcoCheckCircle16 color={item_0 ? 'primary' : 'quaternary'} />} onAdvance={!pending && index_3 === rows.length - 1 ? () => setPending(true) : undefined} onSubmit={label_1 => submitItem(index_3, label_1)} onSubmitLines={lines_0 => submitItemLines(index_3, lines_0)} onEmptyBlur={() => abandonRow(index_3)} onRemove={editable && removable ? () => removeItem(index_3) : undefined} placeholder={NEW_ITEM} removeLabel={`Remove item ${index_3 + 1}`} value={item_0} />)}
          {pending && <ListRow autoFocus className="mt-2 min-h-7" editable
      // Keyed by what has been committed, so each Enter mounts a fresh row that takes the caret
      // from the one it replaces — `autoFocus` only seeds the field's initial state.
      key={`pending-${config.items.length}`} label="New item" marker={<IcoCheckCircle16 color="quaternary" />} onAdvance={() => setPending(true)} onSubmit={submitPending} onSubmitLines={lines_1 => patch({
        items: [...config.items, ...lines_1]
      })} onEmptyBlur={() => setPending(false)} onRemove={() => setPending(false)} placeholder={NEW_ITEM} removeLabel="Remove new item" value="" />}
          {editable && !pending && config.items.length > 0 && <li className="mt-2 flex min-h-7 items-center">
              <LinkButton iconComponent={IcoPlusSmall16} iconPosition="left" onClick={() => setPending(true)} variant="label-2">
                Add an item
              </LinkButton>
            </li>}
        </ul>}
    </BlockShell>;
};
const NEW_ITEM = 'Describe what must be true';
const rowsFor = (items: string[], editable: boolean) => {
  if (items.length) {
    return items;
  }
  if (editable) {
    return [''];
  }
  return [];
};
export default AgentChecklistBlock;